import { Link } from "react-router-dom";
import { CheckCircle, Star } from "@phosphor-icons/react";
import { PageHero, CTASection } from "../components/Sections";
import { Reveal, Overline } from "../components/Reveal";
import { useEventThemes } from "../hooks/usePublicContent";
import { EVENTI, GALLERY, IMAGES } from "../data/content";

export default function Eventi() {
  const themes = useEventThemes();
  return (
    <div data-testid="eventi-page">
      <PageHero
        overline="Eventi Open Bar tematici"
        title="Open Bar tematici che diventano il cuore della serata."
        subtitle="Matrimoni, feste private, eventi aziendali. Progetto l'esperienza dal concept al bicchiere: mise en place, drink d'autore e servizio impeccabile."
        image={IMAGES.event}
      />

      {/* Concept */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src={IMAGES.event} alt="Open bar evento" className="w-full h-[480px] object-cover rounded-sm" />
          </Reveal>
          <Reveal delay={0.15}>
            <Overline>Il concept</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold">Non un bar, ma un'esperienza su misura</h2>
            <p className="mt-6 text-[#A1A1AA] text-lg leading-relaxed">
              Ogni evento ha una storia. Costruisco l'Open Bar attorno ad essa: scelgo il tema, la drink list, l'allestimento e lo stile del servizio per creare un momento coerente, elegante e memorabile.
            </p>
            <p className="mt-4 text-[#A1A1AA] text-lg leading-relaxed">
              Dai classici intramontabili ai signature d'autore, con proposte analcoliche curate allo stesso modo. Il tutto gestito da un team professionale che sa stare in scena.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Tiers */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Overline>Tipologie</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold">Scegli il tuo livello di Open Bar</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {EVENTI.map((e, i) => (
              <Reveal key={e.tier} delay={i * 0.1}>
                <div data-testid={`evento-tier-${e.tier.toLowerCase()}`} className={`relative p-8 rounded-sm h-full flex flex-col ${e.featured ? "bg-gradient-to-b from-[#1a1105] to-[#111113] border border-[#D97736]/50 shadow-[0_0_40px_rgba(217,119,54,0.15)]" : "bg-[#111113] border border-white/5"}`}>
                  {e.featured && (
                    <span className="absolute -top-3 left-8 bg-[#D97736] text-white text-xs font-semibold px-3 py-1 rounded-sm flex items-center gap-1">
                      <Star size={12} weight="fill" /> Più richiesto
                    </span>
                  )}
                  <span className="text-xs tracking-[0.2em] uppercase text-[#D97736] font-semibold">{e.tier}</span>
                  <h3 className="mt-3 font-display text-2xl font-semibold">{e.title}</h3>
                  <p className="mt-2 text-[#F5A623] font-semibold">{e.price}</p>
                  <p className="mt-4 text-[#A1A1AA] leading-relaxed text-sm">{e.description}</p>
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {e.features.map((f) => (
                      <li key={f} className="flex items-center gap-3 text-sm text-[#F8F8F8]">
                        <CheckCircle size={18} weight="fill" className="text-[#D97736] shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contatti"
                    data-testid={`evento-cta-${e.tier.toLowerCase()}`}
                    className={`mt-8 text-center px-6 py-3 rounded-sm font-semibold transition-all ${e.featured ? "bg-[#D97736] text-white hover:bg-[#B85B22]" : "border border-white/20 text-white hover:border-[#D97736] hover:text-[#D97736]"}`}
                  >
                    Richiedi preventivo
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Concept a tema */}
      {themes.length > 0 && (
        <section className="pb-8">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal className="max-w-2xl">
              <Overline>Concept a tema</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold">Format Open Bar pensati per stupire</h2>
              <p className="mt-4 text-[#A1A1AA] text-lg">Ogni concept si può personalizzare sull'evento. Scegli quello che ti ispira o creiamone uno su misura.</p>
            </Reveal>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
              {themes.map((t, i) => (
                <Reveal key={t.id} delay={i * 0.08}>
                  <div data-testid={`event-theme-${t.id}`} className="bg-[#111113] border border-white/5 rounded-sm p-8 h-full hover:border-[#D97736]/40 transition-all">
                    <h3 className="font-display text-2xl font-semibold">{t.title}</h3>
                    <p className="mt-3 text-[#A1A1AA] leading-relaxed">{t.description}</p>
                    <div className="flex flex-wrap gap-2 mt-5">
                      {(t.tags || []).map((tag) => (
                        <span key={tag} className="text-xs bg-[#D97736]/15 text-[#D97736] px-3 py-1 rounded-sm">{tag}</span>
                      ))}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Overline>Gallery</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold">Atmosfere & dettagli</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4">
            {GALLERY.map((src, i) => (
              <Reveal key={src} delay={(i % 3) * 0.08}>
                <div className={`overflow-hidden rounded-sm ${i % 5 === 0 ? "md:row-span-2" : ""}`}>
                  <img src={src} alt={`Evento ${i + 1}`} className={`w-full object-cover hover:scale-105 transition-transform duration-700 ${i % 5 === 0 ? "h-full min-h-[240px]" : "h-56"}`} />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
